import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp, useT } from "@/context/AppContext";
import { computeMonthly } from "@/lib/lease";
import { Check, ShoppingCart, ArrowLeft, ArrowDown, ShieldCheck, Zap, Sparkles, Cpu, Boxes, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { ROICalculator } from "@/components/ROICalculator";
import SubsidyHint from "@/components/SubsidyHint";
import { usePartner } from "@/context/PartnerContext";
import { tr } from "@/lib/i18n";

// Lifestyle image library, keyed by sector slug. Curated Unsplash photos
// that represent real-world deployment contexts for kiosk hardware.
const PX = (id, w = 1400) => `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

const SECTOR_SCENES = {
  hospitality: [
    { img: PX(258154),
      title_nl: "Hotel lobby", title_en: "Hotel lobby", title_de: "Hotel-Lobby", title_fr: "Hall d'hôtel",
      desc_nl: "Gasten checken zelf in, ontvangen hun sleutelkaart en vinden hun kamer, ook na middernacht.",
      desc_en: "Guests check in themselves, collect their key card and find their room, even after midnight.",
      desc_de: "Gäste checken selbst ein, erhalten ihre Keycard, auch nach Mitternacht.",
      desc_fr: "Les clients s'enregistrent seuls, récupèrent leur carte, même après minuit." },
    { img: PX(3769138),
      title_nl: "Snackbar & fastfood", title_en: "Snack bar & fast food", title_de: "Imbiss & Fast Food", title_fr: "Snack & fast-food",
      desc_nl: "Kortere wachtrijen: klanten bestellen en betalen zelf, het personeel focust op bereiden.",
      desc_en: "Shorter queues: customers order and pay themselves while staff focus on preparation.",
      desc_de: "Kürzere Schlangen: Kunden bestellen selbst, das Personal konzentriert sich aufs Zubereiten.",
      desc_fr: "Files d'attente réduites : les clients commandent eux-mêmes, le personnel prépare." },
  ],
  leisure: [
    { img: PX(2901215),
      title_nl: "Camperpark receptie", title_en: "Camper-park reception", title_de: "Wohnmobilpark", title_fr: "Parc camping-cars",
      desc_nl: "Automatische check-in bij aankomst. Slagboom opent na scannen van de reservering.",
      desc_en: "Automatic check-in on arrival. Barrier opens after the booking is scanned.",
      desc_de: "Automatischer Check-in bei Ankunft. Die Schranke öffnet nach Buchungsscan.",
      desc_fr: "Check-in automatique à l'arrivée. La barrière s'ouvre après scan." },
    { img: PX(2662116),
      title_nl: "Camping hoofdingang", title_en: "Campsite main entrance", title_de: "Camping-Eingang", title_fr: "Entrée camping",
      desc_nl: "Gasten betalen voor extra nachten, douchepenningen of stroomtokens zonder de receptie te storen.",
      desc_en: "Guests pay for extra nights, shower tokens or electricity without bothering reception.",
      desc_de: "Gäste zahlen für Zusatznächte, Duschmünzen oder Strom selbst.",
      desc_fr: "Les clients paient nuits supplémentaires, jetons douche et électricité eux-mêmes." },
    { img: PX(1796730),
      title_nl: "Pretpark ticketing", title_en: "Amusement park ticketing", title_de: "Freizeitpark Ticketing", title_fr: "Billetterie parc",
      desc_nl: "Bezoekers kopen tickets en upgrades zonder in de rij te staan bij de kassa.",
      desc_en: "Visitors buy tickets and upgrades without queuing at the cash desk.",
      desc_de: "Besucher kaufen Tickets & Upgrades ohne Warteschlange.",
      desc_fr: "Visiteurs achètent billets & options sans faire la queue." },
  ],
  retail: [
    { img: PX(264636),
      title_nl: "In de winkel", title_en: "Inside the store", title_de: "Im Geschäft", title_fr: "En magasin",
      desc_nl: "Klanten zoeken producten op, vergelijken maten en kleuren of reserveren een artikel.",
      desc_en: "Customers look up products, compare sizes and colours or reserve an item.",
      desc_de: "Kunden recherchieren Produkte, vergleichen Größen und reservieren Artikel.",
      desc_fr: "Clients consultent produits, comparent tailles et réservent articles." },
    { img: PX(3962294),
      title_nl: "Self-checkout", title_en: "Self-checkout", title_de: "Self-Checkout", title_fr: "Caisse libre-service",
      desc_nl: "Minder kassamedewerkers nodig, klanten scannen en betalen zelf hun boodschappen.",
      desc_en: "Fewer cashiers needed, customers scan and pay themselves.",
      desc_de: "Weniger Kassen nötig, Kunden scannen und zahlen selbst.",
      desc_fr: "Moins de caissiers, les clients scannent et paient." },
  ],
  healthcare: [
    { img: PX(236380),
      title_nl: "Ziekenhuis inloop", title_en: "Hospital check-in", title_de: "Kliniken", title_fr: "Accueil hôpital",
      desc_nl: "Patiënten melden zich met hun ID, de kiosk stuurt ze door naar de juiste afdeling.",
      desc_en: "Patients present their ID, the kiosk routes them to the right department.",
      desc_de: "Patienten melden sich mit Ausweis an und werden zur Abteilung geleitet.",
      desc_fr: "Patients présentent leur pièce d'identité, la borne les oriente." },
    { img: PX(3873199),
      title_nl: "Apotheek & kliniek", title_en: "Pharmacy & clinic", title_de: "Apotheke & Klinik", title_fr: "Pharmacie & clinique",
      desc_nl: "Recepten ophalen, afspraken bevestigen of een kort intakegesprek starten via scherm.",
      desc_en: "Pick up prescriptions, confirm appointments or start a short intake.",
      desc_de: "Rezepte abholen, Termine bestätigen, Kurz-Intake.",
      desc_fr: "Retrait d'ordonnances, confirmation de rendez-vous." },
  ],
  aquatic: [
    { img: PX(261410),
      title_nl: "Openbaar zwembad", title_en: "Public pool", title_de: "Öffentliches Schwimmbad", title_fr: "Piscine publique",
      desc_nl: "Bezoekers kopen een toegangskaart en krijgen meteen een QR voor kluis en draaihek.",
      desc_en: "Visitors buy an entry pass and instantly get a QR for locker and turnstile.",
      desc_de: "Besucher kaufen Eintritt, erhalten QR für Schließfach und Drehkreuz.",
      desc_fr: "Les visiteurs achètent l'entrée et reçoivent un QR casier & tourniquet." },
    { img: PX(1263348),
      title_nl: "Aquapark", title_en: "Aqua park", title_de: "Aquapark", title_fr: "Parc aquatique",
      desc_nl: "Tickets plus fast-lane upgrades zijn direct beschikbaar zonder in de rij te staan.",
      desc_en: "Tickets plus fast-lane upgrades available instantly, no queues.",
      desc_de: "Tickets und Fast-Lane-Upgrades sofort verfügbar.",
      desc_fr: "Billets et options fast-lane immédiats." },
  ],
  government: [
    { img: PX(236111),
      title_nl: "Gemeentehuis loket", title_en: "Municipality desk", title_de: "Rathaus", title_fr: "Mairie",
      desc_nl: "Inwoners melden zich zelf aan voor hun afspraak en krijgen een wachtnummer.",
      desc_en: "Citizens check themselves in for their appointment and receive a queue number.",
      desc_de: "Bürger melden sich selbst für Termine an und erhalten Warte-Nummer.",
      desc_fr: "Les citoyens s'enregistrent pour leur rendez-vous et obtiennent un numéro." },
    { img: PX(2451616),
      title_nl: "Paspoort-uitgifte", title_en: "Passport issuance", title_de: "Passausgabe", title_fr: "Délivrance de passeport",
      desc_nl: "ID wordt gescand via NFC of MRZ, foto gemaakt met de ingebouwde camera.",
      desc_en: "ID is scanned via NFC or MRZ; photo captured by the built-in camera.",
      desc_de: "Ausweis per NFC oder MRZ gescannt, Foto mit Kamera.",
      desc_fr: "Pièce d'identité scannée NFC/MRZ, photo prise par la caméra." },
  ],
  construction: [
    { img: PX(1078884),
      title_nl: "Bouwplaats ingang", title_en: "Construction site entry", title_de: "Baustellen-Eingang", title_fr: "Entrée chantier",
      desc_nl: "Medewerkers en onderaannemers klokken in met hun badge. Alleen bevoegden komen binnen.",
      desc_en: "Workers and subcontractors clock in with their badge. Only authorised enter.",
      desc_de: "Mitarbeiter & Subunternehmer stempeln mit Badge ein.",
      desc_fr: "Ouvriers et sous-traitants pointent avec badge." },
    { img: PX(2280571),
      title_nl: "V&G toolbox", title_en: "H&S toolbox", title_de: "Arbeitsschutz", title_fr: "Sécurité",
      desc_nl: "Verplichte veiligheidsinstructies worden per shift afgetekend via het scherm.",
      desc_en: "Mandatory safety briefings signed off per shift on the screen.",
      desc_de: "Pflicht-Sicherheitshinweise werden je Schicht quittiert.",
      desc_fr: "Consignes de sécurité signées à chaque quart." },
  ],
  other: [
    { img: PX(1181406),
      title_nl: "Kantoor receptie", title_en: "Office reception", title_de: "Büro-Empfang", title_fr: "Accueil bureau",
      desc_nl: "Bezoekers registreren zich en de host krijgt direct een notificatie.",
      desc_en: "Visitors register and the host receives an instant notification.",
      desc_de: "Besucher registrieren sich, Gastgeber wird benachrichtigt.",
      desc_fr: "Les visiteurs s'enregistrent, l'hôte est notifié." },
  ],
};

function localize(obj, lang, key) {
  return obj[`${key}_${lang}`] || obj[`${key}_en`] || obj[`${key}_nl`] || "";
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const t = useT();
  const { formatPrice, lang, addToCart, leaseSettings } = useApp();
  const { partner, prefix, isPartner } = usePartner();
  const nav = useNavigate();
  const [hw, setHw] = useState(null);
  const [addons, setAddons] = useState([]);
  const [software, setSoftware] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [mode, setMode] = useState("buy");
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState("");
  const [selAddons, setSelAddons] = useState([]);
  const [selSoftware, setSelSoftware] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/hardware/${id}`);
      setHw(data);
      setImg(data.image_url);
      const swParams = { ...(data.sector_slugs?.[0] ? { sector: data.sector_slugs[0] } : {}) };
      if (partner?.slug) swParams.partner = partner.slug;
      const [a, s, sec] = await Promise.all([
        api.get("/addons"),
        api.get("/software", { params: swParams }),
        api.get("/sectors"),
      ]);
      setAddons(a.data);
      setSoftware(s.data);
      setSectors(sec.data);
    })();
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id, partner?.slug]);

  const sectorMap = useMemo(() => Object.fromEntries(sectors.map((s) => [s.slug, s])), [sectors]);

  const scenes = useMemo(() => {
    if (!hw) return [];
    const out = [];
    (hw.sector_slugs || []).forEach((slug) => {
      (SECTOR_SCENES[slug] || []).forEach((sc) => out.push({ ...sc, sector_slug: slug }));
    });
    // Dedupe by image url and cap at 4
    const seen = new Set();
    return out.filter((x) => (seen.has(x.img) ? false : (seen.add(x.img), true))).slice(0, 4);
  }, [hw]);

  const currentPrice = () => {
    if (!hw) return { once: 0, monthly: 0 };
    if (mode === "buy") return { once: hw.purchase_price, monthly: 0 };
    // Single lease mode → engine computes deterministic monthly from investment
    const { monthly } = computeMonthly(hw.purchase_price || 0, leaseSettings);
    return { once: 0, monthly };
  };
  const priceLabel = () => {
    const p = currentPrice();
    return mode === "buy" ? formatPrice(p.once) : `${formatPrice(p.monthly)}/mnd`;
  };

  const configTotals = useMemo(() => {
    if (!hw) return { once: 0, monthly: 0 };
    const invBase = (hw.purchase_price || 0) * qty;
    let once = mode === "buy" ? invBase : 0;
    let monthly = mode === "buy" ? 0 : computeMonthly(invBase, leaseSettings).monthly;
    selAddons.forEach((aid) => { const a = addons.find((x) => x.id === aid); if (a) once += (a.price || 0) * qty; });
    selSoftware.forEach((sid) => {
      const s = software.find((x) => x.id === sid); if (!s) return;
      once += s.one_time_setup || 0;
      monthly += s.price_monthly || 0;
    });
    return { once, monthly };
  }, [mode, qty, selAddons, selSoftware, hw, addons, software, leaseSettings]);

  if (!hw) return <div className="container-x py-20 text-[#A8B3AD]">Laden…</div>;

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const addConfig = () => {
    const p = currentPrice();
    const hwItem = {
      type: "hardware", ref_id: hw.id, sku: hw.sku, name: hw.name,
      unit_price: p.once, monthly_price: p.monthly, pricing_mode: mode, quantity: qty,
    };
    addToCart(hwItem);
    selAddons.forEach((aid) => {
      const a = addons.find((x) => x.id === aid); if (!a) return;
      addToCart({ type: "addon", ref_id: a.id, sku: a.sku, name: a.name,
        unit_price: a.price, monthly_price: 0, pricing_mode: "buy", quantity: qty });
    });
    selSoftware.forEach((sid) => {
      const s = software.find((x) => x.id === sid); if (!s) return;
      addToCart({ type: "software", ref_id: s.id, sku: s.slug, name: s.name,
        unit_price: s.one_time_setup || 0, monthly_price: s.price_monthly || 0, pricing_mode: "subscription" });
    });
    toast.success("Toegevoegd aan mand");
    nav("/cart");
  };

  return (
    <div data-testid="product-detail-page">
      {/* Back nav */}
      <div className="container-x pt-8">
        <button className="btn-ghost" onClick={() => nav(-1)} data-testid="back-button">
          <ArrowLeft className="w-4 h-4" /> Terug naar catalogus
        </button>
      </div>

      {/* HERO */}
      <section className="container-x py-10 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
          className="lg:col-span-7"
        >
          <div className="product-plate rounded-3xl p-10 min-h-[62vh] grid place-items-center relative overflow-hidden">
            <div className="absolute -top-20 -right-16 w-72 h-72 bg-[#6FAA8A]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-20 w-80 h-80 bg-[#D97742]/8 rounded-full blur-3xl" />
            {img && <img src={img} alt={hw.name} className="relative z-10 max-h-[58vh] max-w-full object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,0.6)]" />}
          </div>
          {hw.gallery?.length > 1 && (
            <div className="flex gap-3 mt-4">
              {hw.gallery.map((g, i) => (
                <button key={i} onClick={() => setImg(g)}
                  className={`w-20 h-20 rounded-lg product-plate p-2 border transition ${img === g ? "border-[#6FAA8A]" : "border-[#243430] hover:border-[#2E3E39]"}`}
                  data-testid={`gallery-thumb-${i}`}>
                  <img src={g} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div className="flex gap-2 mb-4 flex-wrap">
            <span className="chip">
              {(isPartner && partner?.show_vendor_names === false)
                ? "Kiosk"
                : (hw.vendor_slug === "qiox" ? "QIOX" : "Interact Kiosk")}
            </span>
            <span className="chip chip-outline capitalize">{hw.placement}</span>
            <span className="chip chip-outline capitalize">{hw.type}</span>
          </div>
          <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA] tracking-tight leading-[1.05]">
            {(isPartner && partner?.show_vendor_names === false) ? (hw.name || "").replace(/^(QIOX|Interact Kiosk|IK) /, "") : hw.name}
          </h1>
          <p className="text-xs text-[#A8B3AD] mt-2">SKU · <span className="font-mono">{hw.sku}</span></p>
          <p className="mt-6 text-[#A8B3AD] text-lg leading-relaxed">{tr(hw.description_i18n, lang)}</p>

          <div className="mt-8 grid grid-cols-2 gap-3">
            {[
              { icon: <ShieldCheck className="w-4 h-4 text-[#6FAA8A]" />, label: hw.specs?.ip || "Indoor" },
              { icon: <Sparkles className="w-4 h-4 text-[#D97742]" />, label: hw.specs?.brightness || hw.specs?.screen },
              { icon: <Cpu className="w-4 h-4 text-[#6FAA8A]" />, label: hw.specs?.os || "Embedded OS" },
              { icon: <Boxes className="w-4 h-4 text-[#D97742]" />, label: hw.specs?.built_in || hw.specs?.payment || "—" },
            ].map((s, i) => (
              <div key={i} className="card-surface !rounded-lg p-3 flex items-center gap-2 text-xs text-[#A8B3AD]">
                {s.icon} <span className="truncate">{s.label}</span>
              </div>
            ))}
          </div>

          <button onClick={() => document.getElementById("configure")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-accent mt-8" data-testid="scroll-to-configure-button">
            Configureer & bestel <ArrowDown className="w-4 h-4" />
          </button>
        </motion.div>
      </section>

      {/* USE-CASE SCENES */}
      {scenes.length > 0 && (
        <section className="section pt-8" data-testid="usecase-section">
          <div className="container-x">
            <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-3">In gebruik</div>
            <h2 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">Waar je hem inzet</h2>
            <p className="text-[#A8B3AD] mt-3 max-w-2xl">Praktijkvoorbeelden van sectoren waar dit model vandaag al draait.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              {scenes.map((s, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }} viewport={{ once: true }}
                  className="card-surface overflow-hidden group"
                  data-testid={`usecase-scene-${i}`}
                >
                  <div className="h-64 overflow-hidden relative">
                    <img src={s.img} alt={localize(s, lang, "title")}
                      referrerPolicy="no-referrer" loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B110F] via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 chip chip-accent">
                      {tr(sectorMap[s.sector_slug]?.name_i18n, lang) || s.sector_slug}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="display text-xl font-semibold text-[#F4F1EA]">{localize(s, lang, "title")}</h3>
                    <p className="text-sm text-[#A8B3AD] mt-2 leading-relaxed">{localize(s, lang, "desc")}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPECS */}
      {hw.specs && Object.keys(hw.specs).length > 0 && (
        <section className="section pt-0" data-testid="specs-section">
          <div className="container-x">
            <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-3">Specs</div>
            <h2 className="display font-semibold text-3xl text-[#F4F1EA] mb-6">Technische details</h2>
            <div className="card-surface p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
                {Object.entries(hw.specs).map(([k, v]) => (
                  <div key={k} className="py-2 border-b border-[#243430]">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#A8B3AD] font-semibold">{k.replace(/_/g, " ")}</div>
                    <div className="text-[#F4F1EA] font-medium mt-1">{String(v)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CONFIGURE */}
      <section id="configure" className="section pt-0">
        <div className="container-x">
          <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-3">Configureer</div>
          <h2 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">Stel je setup samen</h2>
          <p className="text-[#A8B3AD] mt-2 max-w-xl">Kies koop of lease, voeg software en accessoires toe.</p>

          {/* ROI Calculator, shows payback period based on sector of this product */}
          <div className="mt-8 max-w-2xl">
            <ROICalculator sectorSlugs={hw.sector_slugs || []} kioskCost={hw.purchase_price || 0} />
          </div>

          {/* Subsidy impact preview */}
          <div className="mt-4 max-w-2xl">
            <SubsidyHint amount={hw.purchase_price || 0} variant="banner" />
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mt-10">
            <div className="lg:col-span-2 space-y-8">
              {/* Pricing mode */}
              <div className="card-surface p-6">
                <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A] mb-4">Prijsmodel</div>
                <div className="inline-flex bg-[#0F1613] border border-[#243430] rounded-lg p-1 flex-wrap">
                  {[
                    { key: "buy", label: "Koop" },
                    { key: "lease", label: `Lease ${leaseSettings.default_contract_duration}m` },
                  ].map((m) => (
                    <button key={m.key} onClick={() => setMode(m.key)}
                      className={`px-4 py-2 text-xs rounded-md font-semibold transition ${mode === m.key ? "bg-[#6FAA8A] text-[#0B110F]" : "text-[#A8B3AD] hover:text-[#F4F1EA]"}`}
                      data-testid={`product-pricing-${m.key}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
                <div className="display text-4xl font-semibold text-[#6FAA8A] mt-5">{priceLabel()}</div>
              </div>

              {/* Software */}
              {software.length > 0 && (
                <div className="card-surface p-6" data-testid="software-section">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A]">Software modules</div>
                      <div className="text-[#F4F1EA] font-semibold mt-1">Maak je kiosk slim</div>
                    </div>
                    <span className="chip chip-outline">{software.length} beschikbaar</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {software.map((s) => {
                      const on = selSoftware.includes(s.id);
                      return (
                        <label key={s.id} className={`cursor-pointer p-4 rounded-lg border transition ${on ? "border-[#6FAA8A] bg-[#6FAA8A]/5" : "border-[#243430] hover:border-[#2E3E39]"}`}
                          data-testid={`product-sw-${s.id}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-5 h-5 mt-0.5 rounded border ${on ? "bg-[#6FAA8A] border-[#6FAA8A]" : "border-[#2E3E39]"} grid place-items-center flex-shrink-0`}>
                              {on && <Check className="w-3 h-3 text-[#0B110F]" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-[#F4F1EA]">{s.name}</div>
                              <div className="text-xs text-[#A8B3AD] mt-1 line-clamp-2">{tr(s.description_i18n, lang)}</div>
                              <div className="text-xs text-[#6FAA8A] font-semibold mt-2">
                                {s.price_monthly ? `${formatPrice(s.price_monthly)}/mnd` : ""}
                                {s.one_time_setup ? (s.price_monthly ? ` · setup ${formatPrice(s.one_time_setup)}` : `setup ${formatPrice(s.one_time_setup)}`) : ""}
                              </div>
                            </div>
                          </div>
                          <input type="checkbox" checked={on} onChange={() => toggle(selSoftware, setSelSoftware, s.id)} className="hidden" />
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add-ons */}
              {addons.length > 0 && (
                <div className="card-surface p-6" data-testid="addons-section">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A]">Accessoires</div>
                      <div className="text-[#F4F1EA] font-semibold mt-1">Optionele hardware toevoegen</div>
                    </div>
                    <span className="chip chip-outline">{addons.length} beschikbaar</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {addons.map((a) => {
                      const on = selAddons.includes(a.id);
                      return (
                        <label key={a.id} className={`cursor-pointer p-4 rounded-lg border transition ${on ? "border-[#D97742] bg-[#D97742]/5" : "border-[#243430] hover:border-[#2E3E39]"}`}
                          data-testid={`product-addon-${a.id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <span className="chip chip-outline text-[9px]">{a.category}</span>
                            <div className={`w-5 h-5 rounded border ${on ? "bg-[#D97742] border-[#D97742]" : "border-[#2E3E39]"} grid place-items-center`}>
                              {on && <Check className="w-3 h-3 text-white" />}
                            </div>
                          </div>
                          <div className="text-sm font-semibold text-[#F4F1EA]">{a.name}</div>
                          <div className="text-xs text-[#A8B3AD] mt-1 line-clamp-2">{tr(a.description_i18n, lang) || ""}</div>
                          <div className="text-xs text-[#D97742] font-semibold mt-2">{formatPrice(a.price)}</div>
                          <input type="checkbox" checked={on} onChange={() => toggle(selAddons, setSelAddons, a.id)} className="hidden" />
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Sticky summary */}
            <aside className="card-surface p-6 h-fit lg:sticky lg:top-28" data-testid="order-summary">
              <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A] mb-3">Jouw configuratie</div>
              <div className="text-sm text-[#F4F1EA] font-semibold">{(isPartner && partner?.show_vendor_names === false) ? (hw.name || "").replace(/^(QIOX|Interact Kiosk|IK) /, "") : hw.name}</div>
              <div className="text-xs text-[#A8B3AD]">{hw.sku}</div>

              <div className="flex items-center gap-3 mt-5">
                <span className="text-xs text-[#A8B3AD]">Aantal</span>
                <div className="flex items-center gap-2 ml-auto">
                  <button className="btn-ghost !p-1.5" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-6 text-center text-[#F4F1EA] font-semibold">{qty}</span>
                  <button className="btn-ghost !p-1.5" onClick={() => setQty(qty + 1)} data-testid="qty-plus">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="hairline my-5" />

              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#A8B3AD]">Eenmalig</span><span className="font-semibold text-[#F4F1EA]">{formatPrice(configTotals.once)}</span></div>
                <div className="flex justify-between"><span className="text-[#A8B3AD]">Per maand</span><span className="font-semibold text-[#F4F1EA]">{formatPrice(configTotals.monthly)}</span></div>
              </div>

              <button className="btn-accent w-full justify-center mt-6" onClick={addConfig} data-testid="product-add-cart-button">
                <ShoppingCart className="w-4 h-4" /> Toevoegen aan winkelmand
              </button>
              <div className="text-[10px] text-[#6A7672] text-center mt-3">Prijzen excl. BTW · offerte binnen 24u</div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
